import React, { useEffect } from 'react';
import { Form, Button } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
//import { login } from '../auth'; // login function from react-token-auth
import { useAlert } from '../providers/AlertProvider';
import { useTheme } from './themeControl/ThemeProvider';
import { useAuthProvider } from '../providers/AuthProvider';
import { getOrCreateDeviceId } from './lib/AuthToken';
import logger from "../utils/logger";

import CustomButton from './custom/Button';

import './css/Styles.css';
import twoFaIcon from './img/2fa.png';
import defaultLogo from './img/LogiQOS_logo.png';
import defaultLogoDark from './img/LogiQOS_logo-dark.png';


const LoginPage = () => {

    const { register, handleSubmit, reset, formState: { errors } } = useForm();
    const navigate = useNavigate();
    const { theme } = useTheme();
    const { showAlert } = useAlert();
    const { login, companyLogo } = useAuthProvider();
    const logoSrc = theme === 'dark' ? defaultLogoDark : defaultLogo;

    const [errorMessage, setErrorMessage] = React.useState(null);
    const [isLoggingIn, setIsLoggingIn] = React.useState(false);

    // 2FA state
    const [totpStep, setTotpStep] = React.useState(null); // null | '2fa_required' | '2fa_setup_required'
    const [pendingToken, setPendingToken] = React.useState(null);
    const [pendingEmail, setPendingEmail] = React.useState(null);
    const [totpCode, setTotpCode] = React.useState('');
    const [qrCode, setQrCode] = React.useState(null);
    const [totpError, setTotpError] = React.useState(null);
    const [totpLoading, setTotpLoading] = React.useState(false);

    // Prefill saved email on mount
    useEffect(() => {
        const savedEmail = localStorage.getItem('savedEmail');
        if (savedEmail) {
            reset({ email: savedEmail });
        }
    }, [reset]);

    // Function to Submit the Form
    const loginUser = (data) => {
        setIsLoggingIn(true); // Start loading state
        setErrorMessage(null); // Clear any previous errors

        const body = {
            email: data.email,
            password: data.password,
            device_id: getOrCreateDeviceId()
        };

        // Options to send data to backend
        const requestOptions = {
            method: 'POST',
            headers: {
                'content-type': 'application/json'
            },
            body: JSON.stringify(body)
        };

        // Send Request to backend and log any errors
        fetch('/auth/signin', requestOptions)
            .then(res => res.json())
            .then(async data => {
                if (data.status === '2fa_required') {
                    // ADMIN with 2FA already set up — just needs to enter their code
                    setPendingToken(data.pending_token);
                    setPendingEmail(body.email);
                    setTotpStep('2fa_required');
                    setTotpCode('');
                    setTotpError(null);
                    setIsLoggingIn(false);
                    return;
                }

                if (data.status === '2fa_setup_required') {
                    // ADMIN logging in for the first time — needs to set up an authenticator app
                    setPendingToken(data.pending_token);
                    setPendingEmail(body.email);
                    setTotpLoading(true);
                    fetch('/auth/2fa/setup', {
                        method: 'POST',
                        headers: { 'content-type': 'application/json' },
                        body: JSON.stringify({ pending_token: data.pending_token })
                    })
                        .then(r => r.json())
                        .then(setupData => {
                            setQrCode(setupData.qr_code);
                            setTotpStep('2fa_setup_required');
                            setTotpCode('');
                            setTotpError(null);
                            setTotpLoading(false);
                            setIsLoggingIn(false);
                        });
                    return;
                }

                if (data.session_uuid) {
                    logger.log("Logged in successfully");

                    // Save email locally for future use
                    localStorage.setItem('savedEmail', body.email);

                    // Store both tokens using react-token-auth in frontend
                    await login({
                        session_uuid: data.session_uuid,
                    });

                    // Add delay to allow session to be properly established
                    logger.log("✅ Login successful - Setting up session, please wait...");
                    
                    setTimeout(() => {
                        setIsLoggingIn(false);
                        navigate('/');
                    }, 2000); // 2 second delay for session setup

                } else {
                    setIsLoggingIn(false); // Stop loading
                    // Handle errors returned from the backend
                    showAlert({
                        message: data.error ? data.error : 'Invalid login credentials!',
                        type: 'danger',
                        buttons: [
                            { label: 'Retry', type: 'danger', onClick: () => logger.log('Retrying...') },
                        ]
                    });
                }
            })
            .catch((e) => {
                setIsLoggingIn(false); // Stop loading on error
                showAlert({
                    message: 'Something went wrong!',
                    type: 'danger',
                    buttons: [
                        { label: 'Retry', type: 'danger' },
                    ]
                });
            });

        reset();
    };

    // Handle Enter key press to trigger the form submit
    const handleKeyPress = (event) => {
        if (event.key === 'Enter') {
            handleSubmit(loginUser)(); // Trigger form submit when Enter is pressed
        }
    };

    const completeLoginWithUUID = async (session_uuid) => {
        if (pendingEmail) {
            localStorage.setItem('savedEmail', pendingEmail);
        }
        await login({ session_uuid });
        setTimeout(() => {
            setIsLoggingIn(false);
            navigate('/');
        }, 2000);
    };

    const submitTotpVerify = () => {
        setTotpLoading(true);
        setTotpError(null);
        fetch('/auth/2fa/verify', {
            method: 'POST',
            headers: { 'content-type': 'application/json' },
            body: JSON.stringify({ pending_token: pendingToken, code: totpCode })
        })
            .then(r => r.json())
            .then(async data => {
                setTotpLoading(false);
                if (data.session_uuid) {
                    setTotpStep(null);
                    await completeLoginWithUUID(data.session_uuid);
                } else {
                    setTotpError(data.error || 'Invalid code. Please try again.');
                    setTotpCode('');
                }
            })
            .catch(() => {
                setTotpLoading(false);
                setTotpError('Something went wrong. Please try again.');
            });
    };

    const submitTotpConfirm = () => {
        setTotpLoading(true);
        setTotpError(null);
        fetch('/auth/2fa/confirm', {
            method: 'POST',
            headers: { 'content-type': 'application/json' },
            body: JSON.stringify({ pending_token: pendingToken, code: totpCode })
        })
            .then(r => r.json())
            .then(async data => {
                setTotpLoading(false);
                if (data.session_uuid) {
                    setTotpStep(null);
                    await completeLoginWithUUID(data.session_uuid);
                } else {
                    setTotpError(data.error || 'Invalid code. Please try again.');
                    setTotpCode('');
                }
            })
            .catch(() => {
                setTotpLoading(false);
                setTotpError('Something went wrong. Please try again.');
            });
    };

    const handleTotpKeyPress = (e) => {
        if (e.key === 'Enter') {
            if (totpStep === '2fa_required') submitTotpVerify();
            if (totpStep === '2fa_setup_required') submitTotpConfirm();
        }
    };

    return (
        <div id={theme} className="main-container">
            {/* Fullscreen Background */}
            <div className={theme === 'dark' ? "dark-fullscreen-background" : "fullscreen-background"}></div>

            {/* Login Form */}
            <div
                className="form p-6 rounded-lg shadow-lg"
                style={{
                    position: 'absolute',  // Make it positionable
                    width: '500px',         // Set the width
                    top: '120px',          // Start the form 120px from the top
                    left: 0,              // Align it to the right
                    height: '90vh',        // Set the height to 90% of the viewport height
                    zIndex: 10,            // Ensures it's above the background
                    padding: 50,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center', // Vertically center the content
                    alignItems: 'center',     // Horizontally center the content
                    backgroundColor: theme === 'dark' ? 'rgba(0, 0, 0, 0.9)' : 'rgba(168, 165, 165, 0.9)',
                    border: '0px solid #fff',
                    borderRadius: '0px',
                    pointerEvents: 'auto', // Ensures interactions are enabled
                }}
            >
                
                {/* Title */}
                <span style={{ marginTop: '20px', marginBottom: '-20px'}}>
                <img src={logoSrc} alt="logo" width={300}/>
                <h2 style={{ marginTop: '40px' }}>Management Console</h2>
                </span>
                <form onKeyDown={handleKeyPress}>

                    {/* User's Email */}
                    <Form.Group className="signup-form" style={{ width: '300px'}}>
                        <Form.Label>Email</Form.Label>
                        <Form.Control
                            type="text"
                            placeholder="Enter Email"
                            {...register('email', { required: true, maxLength: 50 })}
                        />
                    </Form.Group>
                    {errors.username && <p className="signup-errors"><small>Email is required</small></p>}
                    {errors.username?.type === "maxLength" && <p className="signup-errors"><small>Max length 50 characters</small></p>}

                    {/* User's Password */}
                    <Form.Group className="signup-form">
                        <Form.Label>Password</Form.Label>
                        <Form.Control
                            type="password"
                            placeholder="Enter Password"
                            {...register('password', { required: true, minLength: 8 })}
                        />
                    </Form.Group>
                    {errors.password && <p className="signup-errors"><small>Password is required</small></p>}
                    {errors.password?.type === "minLength" && <p className="signup-errors"><small>Min length 8 characters</small></p>}

                    {/* Submit Button */}
                    <Form.Group className="signup-button">
                        <CustomButton 
                            onClick={handleSubmit(loginUser)} 
                            variant="primary"
                            disabled={isLoggingIn}
                        >
                            {isLoggingIn ? 'Signing In...' : 'Login'}
                        </CustomButton>
                        
                        {isLoggingIn && (
                            <div className="text-center mt-3">
                                <div className="spinner-border text-primary" role="status" style={{width: '1.5rem', height: '1.5rem'}}>
                                    <span className="visually-hidden">Loading...</span>
                                </div>
                                <p className="text-muted mt-2 mb-0">Setting up your session...</p>
                            </div>
                        )}               
                    </Form.Group>

                </form>

                {/* Popup if login fails */}
                {errorMessage && (
                    <div className="error-popup">
                        <div className="error-message">
                            <p>{errorMessage}</p>
                            <Button onClick={() => setErrorMessage(null)} variant="secondary">Close</Button>
                        </div>
                    </div>
                )}
            </div>

            {/* 2FA Modal — verify existing TOTP code */}
            {totpStep === '2fa_required' && (
                <div style={totpOverlayStyle}>
                    <div style={totpBoxStyle(theme)}>
                        <img src={twoFaIcon} alt="2FA" style={{ width: 80, height: 80, marginBottom: 12 }} />
                        <h4 style={{ marginBottom: '8px' }}>Two-Factor Authentication</h4>
                        <p style={{ fontSize: '0.9rem', marginBottom: '16px', opacity: 0.8 }}>
                            Enter the 6-digit code from your authenticator app.
                        </p>
                        <input
                            type="text"
                            inputMode="numeric"
                            maxLength={6}
                            placeholder="000000"
                            value={totpCode}
                            onChange={e => setTotpCode(e.target.value.replace(/\D/g, ''))}
                            onKeyDown={handleTotpKeyPress}
                            style={totpInputStyle(theme)}
                            autoFocus
                        />
                        {totpError && <p style={{ color: '#e74c3c', fontSize: '0.85rem', marginTop: '8px' }}>{totpError}</p>}
                        <CustomButton
                            onClick={submitTotpVerify}
                            variant="primary"
                            disabled={totpLoading || totpCode.length !== 6}
                            style={{ marginTop: '16px', width: '100%' }}
                        >
                            {totpLoading ? 'Verifying...' : 'Verify'}
                        </CustomButton>
                    </div>
                </div>
            )}

            {/* 2FA Setup Modal — first-time TOTP enrollment */}
            {totpStep === '2fa_setup_required' && (
                <div style={totpOverlayStyle}>
                    <div style={totpBoxStyle(theme)}>
                        <img src={twoFaIcon} alt="2FA" style={{ width: 80, height: 80, marginBottom: 12 }} />
                        <h4 style={{ marginBottom: '8px' }}>Set Up Two-Factor Authentication</h4>
                        <p style={{ fontSize: '0.9rem', marginBottom: '12px', opacity: 0.8 }}>
                            Your account requires 2FA. Scan this QR code with Google Authenticator or Authy, then enter the code below.
                        </p>
                        {qrCode
                            ? <img src={qrCode} alt="TOTP QR Code" style={{ width: 180, height: 180, marginBottom: 16, border: '4px solid white', borderRadius: 8 }} />
                            : <div className="spinner-border text-primary" style={{ marginBottom: 16 }} />
                        }
                        <input
                            type="text"
                            inputMode="numeric"
                            maxLength={6}
                            placeholder="000000"
                            value={totpCode}
                            onChange={e => setTotpCode(e.target.value.replace(/\D/g, ''))}
                            onKeyDown={handleTotpKeyPress}
                            style={totpInputStyle(theme)}
                            autoFocus
                        />
                        {totpError && <p style={{ color: '#e74c3c', fontSize: '0.85rem', marginTop: '8px' }}>{totpError}</p>}
                        <CustomButton
                            onClick={submitTotpConfirm}
                            variant="primary"
                            disabled={totpLoading || totpCode.length !== 6}
                            style={{ marginTop: '16px', width: '100%' }}
                        >
                            {totpLoading ? 'Confirming...' : 'Activate 2FA & Sign In'}
                        </CustomButton>
                    </div>
                </div>
            )}
        </div>
    );
};

const totpOverlayStyle = {
    position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.75)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
};

const totpBoxStyle = (theme) => ({
    backgroundColor: theme === 'dark' ? '#1e1e2e' : '#fff',
    color: theme === 'dark' ? '#fff' : '#333',
    padding: '36px', borderRadius: '12px', width: '360px',
    boxShadow: '0 8px 32px rgba(0,0,0,0.4)', textAlign: 'center',
});

const totpInputStyle = (theme) => ({
    width: '100%', padding: '12px', fontSize: '1.6rem', letterSpacing: '0.5rem',
    textAlign: 'center', border: '2px solid #aaa', borderRadius: '8px',
    backgroundColor: theme === 'dark' ? '#2a2a3e' : '#f5f5f5',
    color: theme === 'dark' ? '#fff' : '#333', outline: 'none',
});

export default LoginPage;
